<script lang="ts">
  import {
    tasks, det, allowWeekendWork, statusRows, cycleStatusDay, floorDay,
    projectStartDate, moveTaskBy, unpinTask, useSwimLanes, twoShifts, projectPhases, wbsNodes, boardSortKey,
    bulkEditMode, bulkSelection, toggleBulkSelect,
  } from "../lib/stores";
  import { phaseColor } from "../lib/phases";
  import { SHIFT_LABEL } from "../lib/shifts";
  import type { Shift } from "../lib/shifts";
  import { allTaskDays, compareCards, groupCardsByPhase, requiredRowValue, resourcesForCards, statusConflicts, statusLabel, statusTone, tasksOnDayRequiringRow } from "../lib/board";
  import type { PhaseGroup } from "../lib/board";
  import { projectDayLabel, projectStartDay, weekdayNameOfProjectDay, weekOfProjectDay } from "../lib/projectDate";
  import StickyCard from "./StickyCard.svelte";

  /** Same day window BoardTable gets for this view (Plan: every day, Week: one week). */
  export let days: number[] = [];

  // Which of `days` is showing. Null follows the clock (like TodayView's selectedDay),
  // so opening the phone view lands on today rather than day one of a month-old plan.
  let pinnedDay: number | null = null;
  $: if (pinnedDay != null && !days.includes(pinnedDay)) pinnedDay = null;
  $: day = pinnedDay ?? ($floorDay != null && days.includes($floorDay) ? $floorDay : days[0] ?? 0);
  $: dayIdx = Math.max(0, days.indexOf(day));
  $: startDay = projectStartDay($projectStartDate);

  function step(delta: number) {
    const next = Math.max(0, Math.min(days.length - 1, dayIdx + delta));
    pinnedDay = days[next];
  }

  $: result = $det;
  $: es = result.ok ? result.ES : {};
  $: allCards = result.ok ? allTaskDays($tasks, result, $allowWeekendWork, $twoShifts) : [];
  $: dayCards = allCards.filter((c) => c.day === day).sort((a, b) => compareCards(a, b, $boardSortKey, es, $wbsNodes));
  $: resources = resourcesForCards(dayCards);

  /** Split into shift buckets first (only when the project uses shifts), phase
   *  swimlanes within each — same two-level layout BoardTable uses for the full board. */
  interface ShiftBucket { shift: Shift | null; groups: PhaseGroup[] }
  $: shiftBuckets = ($twoShifts ? ([1, 2] as const) : ([null] as const)).map(
    (s): ShiftBucket => ({
      shift: s,
      groups: groupCardsByPhase(s ? dayCards.filter((c) => c.shift === s) : dayCards, $useSwimLanes, $projectPhases),
    })
  );

  /** Same keyboard-reschedule contract StickyCard/BoardTable already use — touch has
   *  no drag source anyway, so this (Alt+arrows on a focused card) is the only
   *  rescheduling path this view needs. */
  function onCardMove(e: CustomEvent<{ id: string; dayDelta: number; phaseDelta: number; shiftDelta: number }>) {
    moveTaskBy(e.detail.id, e.detail.dayDelta, e.detail.phaseDelta, e.detail.shiftDelta);
  }
  function onCardUnpin(e: CustomEvent<{ id: string }>) {
    unpinTask(e.detail.id);
  }
</script>

<div class="mday">
  <div class="mday-head">
    <button class="board-btn" type="button" title="Previous day" disabled={dayIdx <= 0} on:click={() => step(-1)}>‹</button>
    <div class="mday-title">
      <div class="mday-date">{projectDayLabel($projectStartDate, day)}</div>
      <div class="mday-sub mono">Week {weekOfProjectDay(day)} · {weekdayNameOfProjectDay(day)}{day === $floorDay ? " · Today" : ""}{day === startDay ? " · Project start" : ""}</div>
    </div>
    <button class="board-btn" type="button" title="Next day" disabled={dayIdx >= days.length - 1} on:click={() => step(1)}>›</button>
    {#if $floorDay != null && day !== $floorDay && days.includes($floorDay)}
      <button class="today-jump" type="button" on:click={() => (pinnedDay = $floorDay)}>Today</button>
    {/if}
  </div>

  {#if $statusRows.length}
    <div class="today-site-grid mday-site">
      {#each $statusRows as row (row.id)}
        {@const explicit = row.values[day] ?? null}
        {@const dependents = tasksOnDayRequiringRow(dayCards, day, row.id)}
        {@const required = requiredRowValue(dependents, row.id)}
        {@const suggested = explicit == null && required != null}
        {@const v = explicit ?? required}
        {@const tone = statusTone(row, v)}
        <button class="site-chip" type="button" class:good={tone === "good"} class:attention={tone === "attention"}
          class:unset={tone === "unset"} class:suggested={suggested}
          on:click={() => cycleStatusDay(row.id, day, required)}>
          <span class="site-chip-label">{row.label}</span>
          <span class="site-chip-value">{statusLabel(row, v)}</span>
        </button>
      {/each}
    </div>
  {/if}

  {#if resources.equipment.length || resources.personnel.length}
    <div class="today-resources">
      {#if resources.equipment.length}
        <div class="resource"><span class="resource-label">🔧 Equipment</span><span class="resource-value">{resources.equipment.join(", ")}</span></div>
      {/if}
      {#if resources.personnel.length}
        <div class="resource"><span class="resource-label">👷 Personnel</span><span class="resource-value">{resources.personnel.join(", ")}</span></div>
      {/if}
    </div>
  {/if}

  {#if !dayCards.length}
    <div class="lean-empty">Nothing scheduled this day.</div>
  {/if}

  {#each shiftBuckets as bucket (bucket.shift ?? "__all__")}
    {#if bucket.shift}
      <h3 class="mday-shift-head">{SHIFT_LABEL[bucket.shift]}</h3>
    {/if}
    {#each bucket.groups as group (group.phase ?? "__all__")}
      <section class="mday-phase">
        {#if group.phase}
          <div class="mday-phase-head">
            <span class="lean-lane-dot" style="background:{phaseColor($projectPhases, group.phase)}"></span>
            <span>{group.phase}</span>
          </div>
        {/if}
        <div class="mday-cards">
          {#each group.cards as entry (entry.task.id)}
            <StickyCard t={entry.task} critical={entry.critical} span={entry.span} spanIdx={entry.index}
              done={entry.done} interactive={true}
              statusConflicts={statusConflicts(entry, $statusRows)}
              selectable={$bulkEditMode} selected={$bulkSelection.has(entry.task.id)}
              on:move={onCardMove}
              on:unpin={onCardUnpin}
              on:toggleSelect={(e) => toggleBulkSelect(e.detail.id)} />
          {/each}
        </div>
      </section>
    {/each}
  {/each}
</div>
